import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

interface TeamMember {
  id: number;
  name: string;
  email: string;
  average: number;
  handicap: number;
  setAverage: number | null;
  calculatedAverage: number | null;
  playerId: number;
  registrationStatus: 'pending' | 'active' | 'cancelled' | 'completed';
  paymentComplete: boolean;
}

interface HandicapConfig {
  base_average: number;
  handicap_percentage: number;
  league_max: number | null;
  handicap_type: 'individual' | 'team_difference';
  team_difference_percentage: number | null;
}

interface Team {
  id: number;
  name: string;
  createdAt: string;
  isScheduled: boolean;
  members: TeamMember[];
  teamStatus: 'eligible' | 'not_eligible';
}

interface ManageTeamsProps {
  leagueId: number;
  onBack: () => void;
  onNavigateToSchedule?: () => void;
  onNavigateToCheckIn?: () => void;
}

const ManageTeams: React.FC<ManageTeamsProps> = ({ leagueId, onBack, onNavigateToSchedule, onNavigateToCheckIn }) => {
  const { t } = useTranslation();
  const [teams, setTeams] = useState<Team[]>([]);
  const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingTeamId, setEditingTeamId] = useState<number | null>(null);
  const [editingTeamName, setEditingTeamName] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [modalType, setModalType] = useState<'success' | 'error'>('success');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState<{id: number, name: string, memberCount: number} | null>(null);
  // Handicap management
  const [handicapConfig, setHandicapConfig] = useState<HandicapConfig | null>(null);
  const [editingSetAvgBowlerId, setEditingSetAvgBowlerId] = useState<number | null>(null);
  const [editingSetAvgValue, setEditingSetAvgValue] = useState<string>('');
  const [pendingHandicaps, setPendingHandicaps] = useState<Record<number, number>>({});
  const [savingHandicaps, setSavingHandicaps] = useState(false);
  const [savingSetAvg, setSavingSetAvg] = useState(false);

  const fetchTeams = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/leagues/${leagueId}/teams`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTeams(data.teams);
      } else {
        showModalMessage(t('admin.manageTeams.messages.fetchFailed'), 'error');
      }
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      showModalMessage(t('admin.manageTeams.messages.fetchFailed'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchHandicapConfig = async () => {
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/admin/leagues/${leagueId}/handicap-config`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHandicapConfig(data);
      }
    } catch (e) { console.error('Failed to fetch handicap config:', e); }
  };

  useEffect(() => {
    fetchTeams();
    fetchHandicapConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leagueId]);

  useEffect(() => {
    // Filter teams based on search term
    if (searchTerm.trim() === '') {
      setFilteredTeams(teams);
    } else {
      const filtered = teams.filter(team => 
        team.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredTeams(filtered);
    }
  }, [teams, searchTerm]);

  const showModalMessage = (message: string, type: 'success' | 'error') => {
    setModalMessage(message);
    setModalType(type);
    setShowModal(true);
  };

  const handleEditTeamName = (teamId: number, currentName: string) => {
    setEditingTeamId(teamId);
    setEditingTeamName(currentName);
  };

  const handleSaveTeamName = async (teamId: number) => {
    if (!editingTeamName.trim()) {
      showModalMessage(t('admin.manageTeams.messages.teamNameEmpty'), 'error');
      return;
    }

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/teams/${teamId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ name: editingTeamName.trim() })
      });

      if (response.ok) {
        // Update the local state
        setTeams(teams.map(team => 
          team.id === teamId 
            ? { ...team, name: editingTeamName.trim() }
            : team
        ));
        setEditingTeamId(null);
        setEditingTeamName('');
        showModalMessage(t('admin.manageTeams.messages.teamNameUpdated'), 'success');
      } else {
        showModalMessage(t('admin.manageTeams.messages.teamNameUpdateFailed'), 'error');
      }
    } catch (error) {
      console.error('Failed to update team name:', error);
      showModalMessage(t('admin.manageTeams.messages.teamNameUpdateFailed'), 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingTeamId(null);
    setEditingTeamName('');
  };

  const handleRemoveTeam = async (teamId: number, teamName: string) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;

    // Double check that all members have pending status
    const allMembersPending = team.members.every(member => 
      member.registrationStatus === 'pending' || member.registrationStatus === 'cancelled'
    );

    if (!allMembersPending) {
      showModalMessage(t('admin.manageTeams.messages.cannotRemoveTeam'), 'error');
      return;
    }

    // Show confirmation modal instead of browser alert
    setTeamToRemove({
      id: teamId,
      name: teamName,
      memberCount: team.members.length
    });
    setShowConfirmModal(true);
  };

  const confirmRemoveTeam = async () => {
    if (!teamToRemove) return;

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/admin/leagues/${leagueId}/teams/${teamToRemove.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const result = await response.json();
        // Remove the team from local state
        setTeams(teams.filter(team => team.id !== teamToRemove.id));
        setShowConfirmModal(false);
        setTeamToRemove(null);
        showModalMessage(
          t('admin.manageTeams.messages.teamRemoved', { 
            teamName: teamToRemove.name, 
            count: result.membersRemoved 
          }), 
          'success'
        );
      } else {
        const errorData = await response.json();
        setShowConfirmModal(false);
        setTeamToRemove(null);
        showModalMessage(errorData.error || t('admin.manageTeams.messages.teamRemovalFailed'), 'error');
      }
    } catch (error) {
      console.error('Failed to remove team:', error);
      setShowConfirmModal(false);
      setTeamToRemove(null);
      showModalMessage(t('admin.manageTeams.messages.teamRemovalFailed'), 'error');
    }
  };

  const cancelRemoveTeam = () => {
    setShowConfirmModal(false);
    setTeamToRemove(null);
  };

  // ─── Set Average editing ────────────────────────────────────────────────────
  const handleStartEditSetAvg = (member: TeamMember) => {
    setEditingSetAvgBowlerId(member.id);
    setEditingSetAvgValue(member.setAverage !== null ? String(member.setAverage) : '');
  };

  const handleSaveSetAvg = async (bowlerId: number) => {
    setSavingSetAvg(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const val = editingSetAvgValue.trim();
      const res = await fetch(`${apiUrl}/admin/leagues/${leagueId}/members/${bowlerId}/set-average`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ set_average: val === '' ? null : Number(val) })
      });
      if (res.ok) {
        setTeams(prev => prev.map(team => ({
          ...team,
          members: team.members.map(m => m.id === bowlerId
            ? { ...m, setAverage: val === '' ? null : Number(val) }
            : m)
        })));
        setEditingSetAvgBowlerId(null);
      } else {
        showModalMessage('Failed to save set average', 'error');
      }
    } catch (e) { showModalMessage('Failed to save set average', 'error'); }
    finally { setSavingSetAvg(false); }
  };

  // ─── Handicap calculation ───────────────────────────────────────────────────
  const calcIndividualHandicap = (avg: number | null, cfg: HandicapConfig): number => {
    if (avg === null || avg <= 0) return 0;
    const raw = (cfg.base_average - avg) * (cfg.handicap_percentage / 100);
    let hdcp = Math.floor(Math.max(0, raw));
    if (cfg.league_max !== null) hdcp = Math.min(hdcp, cfg.league_max);
    return hdcp;
  };

  const handleCalculateHandicaps = (useSetAvg: boolean) => {
    if (!handicapConfig) return;
    const pending: Record<number, number> = {};
    for (const team of teams) {
      for (const member of team.members) {
        const avg = useSetAvg ? member.setAverage : member.calculatedAverage;
        pending[member.id] = calcIndividualHandicap(avg, handicapConfig);
      }
    }
    setPendingHandicaps(pending);
    showModalMessage(t('admin.manageTeams.handicaps.calculated'), 'success');
  };

  const handleSaveHandicaps = async () => {
    const updates = Object.entries(pendingHandicaps).map(([bowlerId, handicap]) => ({
      bowlerId: Number(bowlerId), handicap
    }));
    if (updates.length === 0) return;
    setSavingHandicaps(true);
    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const res = await fetch(`${apiUrl}/admin/leagues/${leagueId}/members/handicaps`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ updates })
      });
      if (res.ok) {
        setTeams(prev => prev.map(team => ({
          ...team,
          members: team.members.map(m => pendingHandicaps[m.id] !== undefined
            ? { ...m, handicap: pendingHandicaps[m.id] }
            : m)
        })));
        setPendingHandicaps({});
        showModalMessage(t('admin.manageTeams.handicaps.saved'), 'success');
      } else {
        showModalMessage(t('admin.manageTeams.handicaps.saveFailed'), 'error');
      }
    } catch (e) { showModalMessage(t('admin.manageTeams.handicaps.saveFailed'), 'error'); }
    finally { setSavingHandicaps(false); }
  };

  const canRemoveTeam = (team: Team) => {
    // Team can only be removed if ALL members have 'pending' or 'cancelled' status
    return team.members.length > 0 && team.members.every(member => 
      member.registrationStatus === 'pending' || member.registrationStatus === 'cancelled'
    );
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'cancelled':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'completed':
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const getTeamStatusColor = (status: string) => {
    switch (status) {
      case 'eligible':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'not_eligible':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/30';
    }
  };

  const renderMemberStatus = (member: TeamMember) => {
    if (member.registrationStatus === 'cancelled') {
      return (
        <div className="text-red-300 text-sm">
          {t('admin.manageTeams.memberStatus.removed')}
        </div>
      );
    } else if (member.registrationStatus === 'pending') {
      return (
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded text-xs border ${getStatusBadgeColor(member.registrationStatus)}`}>
            {member.registrationStatus}
          </span>
          <button
            onClick={handleGoToCheckIn}
            className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-1 rounded text-xs transition duration-200"
          >
            {t('admin.manageTeams.memberStatus.goToCheckin')}
          </button>
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-2">
          <span className={`px-2 py-1 rounded text-xs border ${getStatusBadgeColor(member.registrationStatus)}`}>
            {member.registrationStatus}
          </span>
          {member.paymentComplete && (
            <span className="bg-green-600/20 text-green-300 px-2 py-1 rounded text-xs border border-green-500/30">
              {t('admin.manageTeams.paid')}
            </span>
          )}
        </div>
      );
    }
  };

  const handleGoToSchedule = () => {
    if (onNavigateToSchedule) {
      onNavigateToSchedule();
    } else {
      // Fallback - navigate back
      onBack();
    }
  };

  const handleGoToCheckIn = () => {
    if (onNavigateToCheckIn) {
      onNavigateToCheckIn();
    } else {
      // Fallback - navigate back
      onBack();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p className="text-white">{t('admin.manageTeams.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-purple-800">
      {/* Header */}
      <div className="bg-white/10 backdrop-blur-lg border-b border-white/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center py-6 gap-3">
            <div className="flex items-center space-x-4">
              <button
                onClick={onBack}
                className="text-white hover:text-blue-300 transition duration-200"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <h1 className="text-3xl font-bold text-white">{t('admin.manageTeams.title')}</h1>
            </div>
            {/* Handicap calculation buttons */}
            <div className="flex flex-wrap items-center gap-2">
              {handicapConfig?.handicap_type === 'team_difference' ? (
                <span className="inline-flex items-center gap-1.5 bg-amber-500/20 border border-amber-400/40 text-amber-200 px-3 py-2 rounded-lg text-sm">
                  <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Team diff. league — handicap is calculated per-match
                </span>
              ) : (
                <>
                  <button
                    onClick={() => handleCalculateHandicaps(true)}
                    title={t('admin.manageTeams.handicaps.calcFromSetAvgTitle')}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition duration-200 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    {t('admin.manageTeams.handicaps.calcFromSetAvg')}
                  </button>
                  <button
                    onClick={() => handleCalculateHandicaps(false)}
                    title={t('admin.manageTeams.handicaps.calcFromCalcAvgTitle')}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium px-3 py-2 rounded-lg transition duration-200 flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 002-2z" />
                    </svg>
                    {t('admin.manageTeams.handicaps.calcFromCalcAvg')}
                  </button>
                </>
              )}
              {Object.keys(pendingHandicaps).length > 0 && (
                <button
                  onClick={handleSaveHandicaps}
                  disabled={savingHandicaps}
                  className="bg-green-600 hover:bg-green-700 disabled:opacity-60 text-white text-sm font-bold px-3 py-2 rounded-lg transition duration-200 flex items-center gap-1"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  {savingHandicaps ? t('common.saving') : t('admin.manageTeams.handicaps.saveHandicaps')} ({Object.keys(pendingHandicaps).length})
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

       {/* Help Card */}

      <div
        className="w-16 h-16 bg-yellow-600/30 rounded-full flex items-center justify-center shadow-lg cursor-pointer fixed bottom-8 right-8 z-50"
        onClick={() => setShowHelpModal(true)}
      >
        <span className="text-5xl text-yellow-300 font-bold">?</span>
      </div>
      {/* Help Modal */}
      {showHelpModal && (
        <div
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
          onClick={e => {
            if (e.target === e.currentTarget) setShowHelpModal(false);
          }}
        >
          <div className="bg-white/10 backdrop-blur-lg rounded-xl border border-white/20 max-w-lg w-full mx-4 p-8 relative"
            style={{
              maxHeight: '80vh',
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: '#6366f1 #18181b'
            }}>
            {/* Cool ? Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-blue-600/30 rounded-full flex items-center justify-center shadow-lg">
                <span className="text-5xl text-blue-300 font-bold">?</span>
              </div>
            </div>
            {/* Instructions Body */}
            <h2 className="text-2xl font-bold text-white text-center mb-4">{t('admin.managingLeagueTeamsHelp')}</h2>
            <div className="text-blue-200 text-base whitespace-pre-line">
              {t('admin.managingLeagueTeamsHelpContent')}
            </div>
          
            <button
              onClick={() => setShowHelpModal(false)}
              className="mt-8 w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 transition"
            >
              {t('common.close')}
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Stats */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 space-y-4 sm:space-y-0">
          <div className="flex-1 max-w-md">
            <input
              type="text"
              placeholder={t('admin.manageTeams.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div className="flex items-center space-x-4">
            <div className="bg-white/10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white/20">
              <span className="text-blue-200 text-sm">{t('admin.manageTeams.totalTeams')} </span>
              <span className="text-white font-bold">{filteredTeams.length}</span>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-lg px-4 py-2 border border-white/20">
              <span className="text-blue-200 text-sm">{t('admin.manageTeams.eligible')} </span>
              <span className="text-green-300 font-bold">
                {filteredTeams.filter(team => team.teamStatus === 'eligible').length}
              </span>
            </div>
          </div>
        </div>

        {/* Teams Grid */}
        {filteredTeams.length === 0 ? (
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-8 border border-white/20 text-center">
            <div className="text-4xl mb-2">👥</div>
            {searchTerm ? (
              <>
                <p className="text-blue-200">{t('admin.manageTeams.noTeamsFound', { searchTerm })}</p>
                <p className="text-blue-300 text-sm">{t('admin.manageTeams.adjustSearch')}</p>
              </>
            ) : (
              <>
                <p className="text-blue-200">{t('admin.manageTeams.noTeamsYet')}</p>
                <p className="text-blue-300 text-sm">{t('admin.manageTeams.teamsWillAppear')}</p>
              </>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {filteredTeams.map((team) => (
              <div
                key={team.id}
                className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 hover:bg-white/15 transition duration-200"
              >
                {/* Team Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    {editingTeamId === team.id ? (
                      <div className="flex items-center space-x-2">
                        <input
                          type="text"
                          value={editingTeamName}
                          onChange={(e) => setEditingTeamName(e.target.value)}
                          className="flex-1 px-2 py-1 bg-white/10 border border-white/20 rounded text-white text-lg font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveTeamName(team.id);
                            if (e.key === 'Escape') handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <button
                          onClick={() => handleSaveTeamName(team.id)}
                          className="text-green-400 hover:text-green-300"
                          title={t('admin.manageTeams.saveTeamName')}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="text-red-400 hover:text-red-300"
                          title={t('admin.manageTeams.cancelEdit')}
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-2">
                          <h3 className="text-lg font-bold text-white">{team.name}</h3>
                          <button
                            onClick={() => handleEditTeamName(team.id, team.name)}
                            className="text-blue-400 hover:text-blue-300"
                            title={t('admin.manageTeams.editTeamName')}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                        
                        {/* Remove Team Button - Only show if all members have pending status */}
                        {canRemoveTeam(team) && (
                          <button
                            onClick={() => handleRemoveTeam(team.id, team.name)}
                            className="text-red-400 hover:text-red-300 transition duration-200"
                            title={t('admin.manageTeams.removeTeam')}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Team Status Badges */}
                <div className="flex flex-wrap gap-2 mb-4">
                  <span className={`px-2 py-1 rounded text-xs border font-medium ${getTeamStatusColor(team.teamStatus)}`}>
                    {team.teamStatus === 'eligible' ? t('admin.manageTeams.teamStatus.eligible') : t('admin.manageTeams.teamStatus.notEligible')}
                  </span>
                  <button 
                    onClick={handleGoToSchedule}
                    className={`px-2 py-1 rounded text-xs border font-medium transition duration-200 hover:opacity-80 cursor-pointer ${
                      team.isScheduled 
                        ? 'bg-green-500/20 text-green-300 border-green-500/30 hover:bg-green-500/30'
                        : 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30 hover:bg-yellow-500/30'
                    }`}
                  >
                    {team.isScheduled ? t('admin.manageTeams.scheduleStatus.scheduled') : t('admin.manageTeams.scheduleStatus.notScheduled')}
                  </button>
                </div>

                {/* Schedule Button for Eligible Teams */}
                {team.teamStatus === 'eligible' && (
                  <button
                    onClick={handleGoToSchedule}
                    className={`w-full mb-4 py-2 px-4 rounded-lg text-white font-medium transition duration-200 ${
                      team.isScheduled
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {team.isScheduled ? t('admin.manageTeams.manageSchedule') : t('admin.manageTeams.scheduleTeam')}
                  </button>
                )}

                {/* Team Members */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-blue-200">
                    {t('admin.manageTeams.members', { count: team.members.length })}
                  </h4>
                  {team.members.length === 0 ? (
                    <p className="text-gray-400 text-sm italic">{t('admin.manageTeams.noMembers')}</p>
                  ) : (
                    team.members.map((member) => (
                      <div key={member.id} className="bg-white/5 rounded-lg p-3 border border-white/10">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-white font-medium">{member.name}</p>
                              {!member.paymentComplete && (
                                <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 border border-orange-500/30 whitespace-nowrap">
                                  ⚠ Unpaid
                                </span>
                              )}
                            </div>
                            <p className="text-blue-200 text-sm"><span className='font-bold'>{t('admin.manageTeams.id')}</span> {member.id}</p>
                          </div>
                          {/* Handicap badge (pending or saved) */}
                          {pendingHandicaps[member.id] !== undefined ? (
                            <span className="ml-2 bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded px-2 py-0.5 text-xs font-bold whitespace-nowrap">
                              HDCP* {pendingHandicaps[member.id]}
                            </span>
                          ) : member.handicap > 0 && (
                            <span className="ml-2 bg-blue-500/20 text-blue-200 border border-blue-400/30 rounded px-2 py-0.5 text-xs whitespace-nowrap">
                              HDCP {member.handicap}
                            </span>
                          )}
                        </div>
                        {/* Average stats row */}
                        <div className="flex flex-wrap gap-2 mb-2">
                          {/* Calculated Average (from league games) */}
                          <div className="bg-indigo-500/10 border border-indigo-400/20 rounded px-2 py-1 text-xs">
                            <span className="text-indigo-300 font-medium">{t('admin.manageTeams.handicaps.calculatedAvg')}: </span>
                            <span className="text-white font-semibold">
                              {member.calculatedAverage !== null ? member.calculatedAverage : '—'}
                            </span>
                          </div>
                          {/* Set Average (editable) */}
                          <div className="bg-amber-500/10 border border-amber-400/20 rounded px-2 py-1 text-xs flex items-center gap-1">
                            <span className="text-amber-300 font-medium">{t('admin.manageTeams.handicaps.setAvg')}: </span>
                            {editingSetAvgBowlerId === member.id ? (
                              <>
                                <input
                                  type="number"
                                  value={editingSetAvgValue}
                                  onChange={e => setEditingSetAvgValue(e.target.value)}
                                  className="w-14 bg-white/10 border border-white/20 rounded text-white text-xs px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-amber-400"
                                  min={0}
                                  max={300}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveSetAvg(member.id); if (e.key === 'Escape') setEditingSetAvgBowlerId(null); }}
                                  autoFocus
                                />
                                <button
                                  onClick={() => handleSaveSetAvg(member.id)}
                                  disabled={savingSetAvg}
                                  className="text-green-400 hover:text-green-300 disabled:opacity-50"
                                  title={t('common.save')}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                                <button
                                  onClick={() => setEditingSetAvgBowlerId(null)}
                                  className="text-red-400 hover:text-red-300"
                                  title={t('common.cancel')}
                                >
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </>
                            ) : (
                              <>
                                <span className="text-white font-semibold">
                                  {member.setAverage !== null ? member.setAverage : '—'}
                                </span>
                                <button
                                  onClick={() => handleStartEditSetAvg(member)}
                                  className="text-amber-400 hover:text-amber-300 ml-0.5"
                                  title={t('admin.manageTeams.handicaps.editSetAvg')}
                                >
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                                  </svg>
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        {renderMemberStatus(member)}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Success/Error Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 max-w-md w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              {modalType === 'success' ? (
                <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              ) : (
                <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              )}
              <h3 className="text-lg font-medium text-white">
                {modalType === 'success' ? t('admin.manageTeams.modal.success') : t('admin.manageTeams.modal.error')}
              </h3>
            </div>
            <p className="text-blue-200 mb-6">{modalMessage}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowModal(false)}
                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
              >
                {t('admin.manageTeams.modal.ok')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal for Team Removal */}
      {showConfirmModal && teamToRemove && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white/10 backdrop-blur-lg rounded-xl p-6 border border-white/20 max-w-lg w-full mx-4">
            <div className="flex items-center space-x-3 mb-4">
              <div className="flex-shrink-0 w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-white">{t('admin.manageTeams.confirmRemoval.title')}</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-blue-200 mb-4" dangerouslySetInnerHTML={{ 
                __html: t('admin.manageTeams.confirmRemoval.confirmMessage', { teamName: teamToRemove.name }) 
              }} />
              
              <div className="bg-red-500/10 border border-red-400/30 rounded-lg p-4 mb-4">
                <h4 className="text-red-300 font-medium mb-2">{t('admin.manageTeams.confirmRemoval.warningTitle')}</h4>
                <ul className="text-red-200 text-sm space-y-1">
                  <li>• {t('admin.manageTeams.confirmRemoval.warningItems.removeMembers', { count: teamToRemove.memberCount })}</li>
                  <li>• {t('admin.manageTeams.confirmRemoval.warningItems.deleteMatches')}</li>
                  <li>• {t('admin.manageTeams.confirmRemoval.warningItems.unassignTeam')}</li>
                </ul>
              </div>
              
              <p className="text-yellow-300 text-sm font-medium">
                {t('admin.manageTeams.confirmRemoval.cannotUndo')}
              </p>
            </div>
            
            <div className="flex space-x-3">
              <button
                onClick={cancelRemoveTeam}
                className="flex-1 bg-gray-600 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
              >
                {t('admin.manageTeams.confirmRemoval.cancelButton')}
              </button>
              <button
                onClick={confirmRemoveTeam}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-200"
              >
                {t('admin.manageTeams.confirmRemoval.confirmButton')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageTeams;